import { ChangeEvent, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import { Select, SelectValue, SelectTrigger, SelectItem, SelectContent, Button, ChevronRight } from '@mesh.ai/rabbit';
import { LabelFieldWrapper } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/LabelFieldWrapper';
import { SectionWrapper } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/SectionWrapper';

import { Calendar } from 'assets/assetsV1/iconComponents';
import { ArrowDropRight } from 'assets/assetsV1/iconComponents/ArrowDropRight';

import { DatePicker } from 'uiToolkitV1/DatePicker';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

const TRIGGERED_BY_LABELS = {
  WORK_ANNIVERSARY: {
    label: 'Employee work-anniversary',
    subLabel: "opens around each employee's anniversary date",
  },
  START_DATE: {
    label: 'Employee start-date',
    subLabel: "Opens a set number of days after the employee's start date",
  },
  FIXED_INTERVAL: {
    label: 'Fixed interval',
    subLabel: 'Repeats automatically every X months / each year',
  },
};

export const CompensationCycleDetailsSection = () => {
  // TODO: Move all the states to server state once we have the API ready
  const [compensationCycleName, setCompensationCycleName] = useState('');
  const [compensationCycleType, setCompensationCycleType] = useState<'ONE_TIME' | 'CONTINUOUS'>('ONE_TIME');
  const [linkedToPerformanceCycle, setLinkedToPerformanceCycle] = useState<'NONE' | 'PERFORMANCE_CYCLE'>('NONE');
  const [launchTiming, setLaunchTiming] = useState<Date | null>(new Date());
  const [triggeredBy, setTriggeredBy] = useState<'WORK_ANNIVERSARY' | 'START_DATE' | 'FIXED_INTERVAL'>(
    'WORK_ANNIVERSARY'
  );

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCompensationCycleName(e.target.value);
  };

  return (
    <SectionWrapper
      title="Cycle details"
      subTitle="Define the name, type and participant group for this compensation cycle."
    >
      <LabelFieldWrapper label="Compensation cycle name" gridTemplateColumns="320px 1fr" gridGap={0}>
        <InputV2
          placeholder="Enter compensation cycle name"
          isDashed
          onChange={handleNameChange}
          value={compensationCycleName}
          width="400px"
        />
      </LabelFieldWrapper>
      <LabelFieldWrapper label="Compensation cycle type" gridTemplateColumns="320px 1fr" gridGap={0}>
        <Flex flexDir="column" gap="8px">
          <Select
            value={compensationCycleType}
            onValueChange={(value) => setCompensationCycleType(value as 'ONE_TIME' | 'CONTINUOUS')}
          >
            <SelectTrigger style={{ width: '400px' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ minWidth: 'fit-content' }}>
              <SelectItem value="ONE_TIME">One-time</SelectItem>
              <SelectItem value="CONTINUOUS">Continuous (based on triggers)</SelectItem>
            </SelectContent>
          </Select>
          {compensationCycleType === 'CONTINUOUS' && (
            <Flex h="40px" w="400px" align="center" gap="8px" sx={{ '& > div': { display: 'flex', flex: 1 } }}>
              <TextBodyMedium color="text.g2">Triggered by</TextBodyMedium>
              <Select
                value={triggeredBy}
                onValueChange={(value) => setTriggeredBy(value as 'WORK_ANNIVERSARY' | 'START_DATE' | 'FIXED_INTERVAL')}
              >
                <SelectTrigger style={{ flex: 1, fontStyle: 'italic' }}>
                  {TRIGGERED_BY_LABELS[triggeredBy].label}
                </SelectTrigger>
                <SelectContent style={{ minWidth: 'fit-content' }}>
                  {Object.entries(TRIGGERED_BY_LABELS).map(([key, { label, subLabel }]) => (
                    <SelectItem value={key} style={{ paddingRight: '40px' }}>
                      <Flex flexDir="column" gap="2px">
                        <TextBodyMedium>{label}</TextBodyMedium>
                        <TextLabelSmall>{subLabel}</TextLabelSmall>
                      </Flex>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Flex>
          )}
        </Flex>
      </LabelFieldWrapper>
      <LabelFieldWrapper label="Linked to a performance cycle" gridTemplateColumns="320px 1fr" gridGap={0}>
        <Select
          value={linkedToPerformanceCycle}
          onValueChange={(value) => setLinkedToPerformanceCycle(value as 'NONE' | 'PERFORMANCE_CYCLE')}
        >
          <SelectTrigger style={{ width: '400px' }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent style={{ minWidth: 'fit-content' }}>
            <SelectItem value="NONE">None</SelectItem>
            <SelectItem value="PERFORMANCE_CYCLE">Performance cycle</SelectItem>
          </SelectContent>
        </Select>
      </LabelFieldWrapper>
      <LabelFieldWrapper label="Launch timing" gridTemplateColumns="320px 1fr" gridGap={0}>
        <DatePicker
          selectedDate={launchTiming}
          onChange={(date) => setLaunchTiming(date)}
          customInputProps={{
            width: '400px',
            justifyContent: 'space-between !important',
            StartIcon: null,
            EndIcon: Calendar,
          }}
        />
      </LabelFieldWrapper>
      <LabelFieldWrapper
        label={null}
        labelComp={
          <Flex flexDir="column" gap="2px">
            <TextBodyMedium color="text.g2">Workflow stakeholders</TextBodyMedium>
            <TextLabelSmall color="text.g2" letterSpacing="0px">
              Who is part of the cycle?
            </TextLabelSmall>
          </Flex>
        }
        gridTemplateColumns="320px 1fr"
        gridGap={0}
      >
        <Flex
          flexDir="column"
          gap="12px"
          background="linear-gradient(180deg, #F5F7F9 0%, rgba(245, 247, 249, 0.00) 100%)"
          borderRadius="8px"
          border="1px solid"
          borderColor="border.EBEFF3"
          p="12px 12px 16px 12px"
          w="400px"
        >
          <TextLabel fontSize="14px">
            <Flex align="center" wrap="wrap">
              50 candidates
              <ArrowDropRight boxSize="16px" />
              10 recommendation
              <ArrowDropRight boxSize="16px" />
              3 approvers
              <ArrowDropRight boxSize="16px" />1 publisher
            </Flex>
          </TextLabel>
          <Flex align="center" justifyContent="flex-end">
            <Button variant="text" size="small" iconRight={ChevronRight} style={{ padding: '0px' }} onClick={() => {}}>
              Edit
            </Button>
          </Flex>
        </Flex>
      </LabelFieldWrapper>
      <LabelFieldWrapper label="Currency" gridTemplateColumns="320px 1fr" gridGap={0}>
        <Flex flexDir="column" gap="8px">
          <Select value="USD">
            <SelectTrigger style={{ width: '400px' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ minWidth: 'fit-content' }}>
              <SelectItem value="USD">$ (Dollar)</SelectItem>
              <SelectItem value="EUR">€ (Euro)</SelectItem>
              <SelectItem value="GBP">£ (Pound)</SelectItem>
              <SelectItem value="INR">₹ (Rupee)</SelectItem>
              <SelectItem value="AUD">$ (Australian Dollar)</SelectItem>
              <SelectItem value="CAD">$ (Canadian Dollar)</SelectItem>
              <SelectItem value="CHF">₣ (Swiss Franc)</SelectItem>
              <SelectItem value="CNY">¥ (Chinese Yuan)</SelectItem>
            </SelectContent>
          </Select>
          <Flex align="center" justifyContent="flex-end" w="400px">
            <Button variant="text" size="small" iconRight={ChevronRight} style={{ padding: '0px' }}>
              Conversion rate
            </Button>
          </Flex>
        </Flex>
      </LabelFieldWrapper>
    </SectionWrapper>
  );
};
